import { FunctionComponent, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { classNames } from "primereact/utils";

import { faChevronDown, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { translationKeys } from "../../../models/app-translation-keys";
import { Device } from "../../../stores";
import { useStore } from "../../../stores/StoreContext";
import { TranslationType } from "../TranslationHeader/TranslationHeader";

import "./TranslationContentLayout.scss";

interface ITranslationContentLayoutProps {
  sourceArea: ReactNode;
  targetArea: ReactNode;
  type: TranslationType;
  isTranslating: boolean;
  showTranslationButton?: boolean;
  customLoadingTemplate?: ReactNode;
  disabled?: boolean;
  translate?: () => void;
}

const TranslationContentLayoutRaw: FunctionComponent<ITranslationContentLayoutProps> = props => {
  const { t } = useTranslation();
  const { breakpointStore } = useStore();
  const featureTranslationKeys = translationKeys.feature.translation;

  const translateButtonIconMemo = useMemo(() => {
    const isTabletOrSmaller = breakpointStore.device != null && breakpointStore.isSmallerThan(Device.Tablet);
    return <FontAwesomeIcon icon={isTabletOrSmaller ? faChevronDown : faChevronRight} />;
  }, [breakpointStore.device]);

  const targetAreaMemo = useMemo(() => {
    if (props.isTranslating) {
      return (
        <div className="translation-progress-visualization-container">
          {props.customLoadingTemplate ?? <ProgressSpinner />}
        </div>
      );
    }

    if (props.targetArea) {
      return props.targetArea;
    }

    return (
      <div className="placeholder">
        <span className="text">{t(featureTranslationKeys.targetArea.placeholder)}</span>
      </div>
    );
  }, [props.isTranslating, props.customLoadingTemplate, props.targetArea]);

  return (
    <div
      className={classNames("translation-content-layout", {
        "show-translation-button": props.showTranslationButton,
      })}
    >
      <div className="source-area">{props.sourceArea}</div>

      <div className="translate-button-container">
        {props.showTranslationButton && (
          <Button
            data-testid="translate-button"
            icon={translateButtonIconMemo}
            disabled={props.disabled}
            rounded
            text={props.disabled}
            aria-label="Translate"
            onClick={props.translate}
            tooltip={t(
              props.type === TranslationType.Text
                ? featureTranslationKeys.actions.translateText
                : featureTranslationKeys.actions.translateFile,
            )}
            tooltipOptions={{ position: "top" }}
          />
        )}
      </div>

      <div className="target-area">{targetAreaMemo}</div>
    </div>
  );
};

export const TranslationContentLayout = observer(TranslationContentLayoutRaw);
